import { action, computed, makeObservable, observable, observableRef } from 'mobx';

import { Store } from '../../store/store';
import { Message, StoreNode } from '../../store';
import Routes, { BackRoutes, type LocationDescriptor } from '../../routes';
import { UploadTask } from '../../services/upload';
import {
  clamp,
  GuidedTrainingDomSync,
  getGuidedTrainingCoachPosition,
  getGuidedTrainingStorageKey,
  getGuidedTrainingTargetRect,
  guidedTrainingTargetSelector,
  type GuidedTrainingCoachPosition,
  type GuidedTrainingRect,
} from '../../components/guidedTraining/guidedTrainingCore';

type UploadGuidedTrainingRect = GuidedTrainingRect;
type CoachPosition = GuidedTrainingCoachPosition;

type Step = {
  title: string;
  description: string;
  targetId?: string;
  primaryLabel: string;
  page: 'upload' | 'dashboard';
};

type OpenPayload = {
  teamId: string;
};

type ProgressPhase = 'upload' | 'uploading' | 'dashboard';

type Progress = {
  phase: ProgressPhase;
  currentStepIndex: number;
  teamId: string | null;
  practiceTaskId: string | null;
  isUploadSubmitting: boolean;
};

const activeBodyClass = 'upload-guided-training-active';
const progressStoragePrefix = 'guidedTraining.upload';
const positionRetryDelayMs = 50;
const positionRetryAttempts = 10;

const getTargetRect = (targetId?: string): UploadGuidedTrainingRect | null =>
  getGuidedTrainingTargetRect(targetId);

const getCoachPosition = (rect: UploadGuidedTrainingRect | null): CoachPosition =>
  getGuidedTrainingCoachPosition(rect, {
    maxWidth: 720,
    height: 190,
    defaultTopRatio: 0.2,
    defaultTopMin: 104,
  });

export class UploadGuidedTrainingState
  extends StoreNode {

  readonly nodeType = 'UploadGuidedTraining';

  constructor(store: Store) {
    super(store);
    makeObservable<this, 'resetRuntime' | 'finishDashboardStep' | 'uploadPracticeVideo' | 'restoreProgress'>(this, {
      isArmed: observable,
      isVisible: observable,
      currentStepIndex: observable,
      teamId: observable,
      practiceTaskId: observable,
      isUploadSubmitting: observable,
      targetRect: observableRef,
      coachPosition: observableRef,
      steps: computed,
      currentStep: computed,
      totalSteps: computed,
      practiceTask: computed,
      uploadRoute: computed,
      selectedUploadTeamId: computed,
      open: action,
      handleUploadPageMounted: action,
      handleUploadPageUnmounted: action,
      handleTeamDashboardMounted: action,
      handleTeamDashboardUnmounted: action,
      close: action,
      skip: action,
      pause: action,
      resetRuntime: action,
      next: action,
      back: action,
      handlePracticeFileSelected: action,
      syncPosition: action,
      finishDashboardStep: action,
      uploadPracticeVideo: action,
      restoreProgress: action,
    });
    this.receive(this.receiver, ['UploadService:taskCompleted']);
    this.domSync = new GuidedTrainingDomSync({
      syncPosition: this.syncPosition,
      activeBodyClass,
      onKeyDown: this.handleKeyDown,
      retryAttempts: positionRetryAttempts,
      retryDelayMs: positionRetryDelayMs,
    });
  }

  isArmed = false;
  isVisible = false;
  currentStepIndex = 0;
  teamId: string | null = null;
  practiceTaskId: string | null = null;
  isUploadSubmitting = false;
  targetRect: UploadGuidedTrainingRect | null = null;
  coachPosition: CoachPosition = { top: 120, left: 16, width: 720 };

  private readonly domSync: GuidedTrainingDomSync;

  get steps(): Step[] {
    return [
      {
        title: 'Choose the named Library',
        description: 'First confirm the "Upload to" destination. This makes sure the recording appears in the correct user or agency Library.',
        targetId: 'upload-training-destination',
        primaryLabel: 'Next',
        page: 'upload',
      },
      {
        title: 'Upload a practice video',
        description: 'Drag and drop a video or audio file, or click Select Files. CLIPr supports major formats like MP4, MP3, WAV, and AVI.',
        targetId: 'upload-training-file-dropzone',
        primaryLabel: 'Choose video',
        page: 'upload',
      },
      {
        title: 'Confirm the Job Specialty',
        description: 'This dropdown determines the structure of the generated report. Check the default before uploading and choose the format your agency actually needs.',
        targetId: 'upload-training-speciality',
        primaryLabel: 'Upload video',
        page: 'upload',
      },
      {
        title: 'Keep working while CLIPr processes',
        description: 'Uploads often take a minute or more. The progress stays visible in your Library, and CLIPr will notify you when the report is ready.',
        primaryLabel: 'Got it',
        page: 'dashboard',
      },
    ];
  }

  get currentStep(): Step | null {
    return this.steps[this.currentStepIndex] || null;
  }

  get totalSteps(): number {
    return this.steps.length;
  }

  get practiceTask(): UploadTask | null {
    if (!this.practiceTaskId) return null;

    return this.store.uploadService.tasks.find(task => task.id === this.practiceTaskId) || null;
  }

  get uploadRoute(): LocationDescriptor | null {
    if (!this.teamId) return null;

    return BackRoutes[Routes.teamUpload()].route({
      currentPath: Routes.teamDashboard(this.teamId),
      teamId: this.teamId,
    });
  }

  get selectedUploadTeamId(): string | null {
    return this.store.uploadPage.teamId || this.teamId;
  }

  private get progressStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix);
  }

  private get completedStorageKey(): string {
    return getGuidedTrainingStorageKey(this.store, progressStoragePrefix, 'completed');
  }

  get hasCompleted(): boolean {
    return !!this.store.storage.getLocal(this.completedStorageKey, true);
  }

  open(payload: OpenPayload) {
    if (this.hasCompleted) return;

    this.clearProgress();
    this.resetRuntime();
    this.isArmed = true;
    this.teamId = payload.teamId;
    this.currentStepIndex = 0;
    this.saveProgress('upload');
  }

  handleUploadPageMounted(): boolean {
    const restoredProgress = !this.isArmed && this.restoreProgress();
    if (!this.isArmed && !restoredProgress) {
      const teamId = this.selectedUploadTeamId;
      if (!teamId || this.hasCompleted)
        return false;

      this.open({ teamId });
    }

    if (this.currentStep?.page === 'dashboard') {
      if (this.teamId)
        this.store.goTo(Routes.teamDashboard(this.teamId));

      return true;
    }

    if (this.isUploadSubmitting && this.practiceTask)
      return true;

    if (restoredProgress && this.currentStepIndex > 1) {
      this.store.uploadPage.fileComponent.resetForm();
      this.currentStepIndex = 1;
      this.saveProgress('upload');
    } else if (this.currentStepIndex > 1 && !this.hasSelectedPracticeFile()) {
      this.currentStepIndex = 1;
      this.saveProgress('upload');
    }

    this.isVisible = true;
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync(true);

    return true;
  }

  handleUploadPageUnmounted() {
    if (this.isUploadSubmitting) {
      this.pause();
      return;
    }

    if (this.currentStep?.page === 'dashboard') return;

    this.pause();
  }

  handleTeamDashboardMounted(teamId: string | null): boolean {
    if (!this.isArmed && !this.restoreProgress())
      return false;

    if (!this.isArmed || this.teamId !== teamId)
      return false;

    if (this.currentStep?.page !== 'dashboard') {
      const route = this.uploadRoute;
      if (route)
        this.store.goTo(route);

      return true;
    }

    this.isVisible = true;
    this.domSync.bindListeners();
    this.domSync.schedulePositionSync(true);

    return true;
  }

  handleTeamDashboardUnmounted(teamId: string | null) {
    if (!this.isArmed || this.currentStep?.page !== 'dashboard' || this.teamId !== teamId) return;

    this.pause();
  }

  close = () => {
    this.clearProgress();
    this.resetRuntime();
  }

  skip = () => {
    const teamId = this.teamId || this.selectedUploadTeamId;
    this.finishDashboardStep();

    if (teamId)
      this.store.goTo(Routes.teamDashboard(teamId));
  }

  pause = () => {
    if (this.isArmed)
      this.saveProgress();

    this.isVisible = false;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  private resetRuntime = () => {
    this.isArmed = false;
    this.isVisible = false;
    this.currentStepIndex = 0;
    this.teamId = null;
    this.practiceTaskId = null;
    this.isUploadSubmitting = false;
    this.targetRect = null;
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  next = () => {
    switch (this.currentStepIndex) {
      case 0:
        this.currentStepIndex = 1;
        break;
      case 1:
        if (!this.hasSelectedPracticeFile()) {
          this.openFilePicker();
          return;
        }
        this.currentStepIndex = 2;
        break;
      case 2:
        this.uploadPracticeVideo();
        return;
      default:
        this.finishDashboardStep();
        return;
    }

    this.saveProgress();
    this.domSync.schedulePositionSync(this.currentStepIndex === 2);
  }

  back = () => {
    if (this.currentStep?.page === 'dashboard') return;

    this.currentStepIndex = Math.max(0, this.currentStepIndex - 1);
    this.saveProgress();
    this.domSync.schedulePositionSync(true);
  }

  handlePracticeFileSelected() {
    if (!this.isArmed || this.currentStepIndex < 1 || this.currentStepIndex > 2) return;

    this.currentStepIndex = 2;
    this.saveProgress('upload');
    this.domSync.schedulePositionSync(true);
  }

  syncPosition = () => {
    const rect = getTargetRect(this.currentStep?.targetId);
    this.targetRect = rect;
    this.coachPosition = getCoachPosition(rect);

    if (!rect && this.currentStep?.targetId && this.domSync.canRetry)
      this.domSync.schedulePositionRetry();
  }

  private hasSelectedPracticeFile(): boolean {
    return this.store.uploadPage.fileComponent.stagedItems.length > 0;
  }

  private openFilePicker() {
    const input = document.querySelector<HTMLInputElement>(
      `${guidedTrainingTargetSelector('upload-training-file-dropzone')} input[type="file"]`
    );
    input?.click();
  }

  private get isOnUploadPage(): boolean {
    const pathname = this.store.history.location.pathname;
    return pathname === Routes.upload() || pathname.startsWith(`${Routes.upload()}/`);
  }

  private finishDashboardStep() {
    this.store.storage.setLocal(this.completedStorageKey, true, true);
    this.clearProgress();
    this.resetRuntime();
  }

  private uploadPracticeVideo() {
    const { fileComponent } = this.store.uploadPage;
    const existingTaskIds = new Set(this.store.uploadService.tasks.map(task => task.id));

    fileComponent.submit();

    const newPracticeTask = this.store.uploadService.tasks.find(task =>
      !existingTaskIds.has(task.id)
    );

    if (!newPracticeTask) {
      this.domSync.schedulePositionSync(true);
      return;
    }

    this.teamId = newPracticeTask.teamId || this.selectedUploadTeamId;
    this.practiceTaskId = newPracticeTask.id;
    this.currentStepIndex = 3;
    this.isVisible = false;
    this.isUploadSubmitting = true;
    this.saveProgress('uploading');
    this.domSync.unbindListeners();
    this.domSync.cancelPositionSync();
  }

  private saveProgress(phase = this.progressPhase) {
    this.store.storage.setLocal(this.progressStorageKey, {
      phase,
      currentStepIndex: this.currentStepIndex,
      teamId: this.teamId,
      practiceTaskId: this.practiceTaskId,
      isUploadSubmitting: this.isUploadSubmitting,
    }, true);
  }

  private loadProgress(): Progress | null {
    return this.store.storage.getLocal<Progress>(this.progressStorageKey, true);
  }

  private clearProgress() {
    this.store.storage.removeLocal(this.progressStorageKey);
  }

  private restoreProgress(): boolean {
    if (this.hasCompleted)
      return false;

    const progress = this.loadProgress();
    if (!progress || !progress.teamId)
      return false;

    this.teamId = progress.teamId;
    this.practiceTaskId = progress.practiceTaskId;
    this.currentStepIndex = clamp(progress.currentStepIndex, 0, this.steps.length - 1);
    this.isUploadSubmitting = progress.isUploadSubmitting && !!this.practiceTask;
    this.isArmed = true;

    if (progress.phase === 'dashboard') {
      this.currentStepIndex = 3;
      this.isUploadSubmitting = false;
    } else if (progress.phase === 'uploading' && !this.practiceTask) {
      this.currentStepIndex = 1;
      this.practiceTaskId = null;
      this.isUploadSubmitting = false;
    } else if (this.currentStep?.page !== 'upload') {
      this.currentStepIndex = 0;
    }

    this.saveProgress();

    return true;
  }

  private get progressPhase(): ProgressPhase {
    if (this.currentStep?.page === 'dashboard')
      return 'dashboard';

    return this.isUploadSubmitting ? 'uploading' : 'upload';
  }

  private handleKeyDown = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape')
      this.pause();
  }

  private receiver = (msg: Message) => {
    if (msg.type !== 'taskCompleted') return;
    if (!this.isUploadSubmitting) return;
    if (!this.practiceTask?.isCompleted) return;

    this.isUploadSubmitting = false;
    this.currentStepIndex = 3;
    this.saveProgress('dashboard');

    if (!this.isOnUploadPage) {
      this.pause();
      return;
    }

    if (this.teamId) {
      this.store.goTo(Routes.teamDashboard(this.teamId));
      return;
    }

    this.close();
  }
}
